import React, { useState, useRef } from 'react';
import { testPrompt } from '../services/api';

// ── Sample payload ─────────────────────────────────────────────────────────
const SAMPLE = {
  expenditure: {
    exp_id:           'TEST-001',
    amt:              1250.00,
    awd_num:          'AWD-E5678',
    exp_cat:          'Laboratory Supplies',
    exp_type:         'Scientific Lab Consumables',
    exp_txn_long_desc:'Purchase of reagents and glassware for aerodynamics wind-tunnel experiments. PI: Kroo, Ilan. Authorized by dept admin.',
    task_org_name:    'Department of Aeronautics and Astronautics',
    proj_name:        'Advanced Aerodynamics Research Project',
    task_name:        'Wind Tunnel Experiment Series A',
    proj_owner:       'Kroo, Ilan',
    task_owner:       'Kroo, Ilan',
  },
  candidates: [
    {
      awd_num:      'AWD-000101',
      awd_org:      'School of Engineering',
      awd_start:    '2015-01-01',
      awd_name:     'Ilan Kroo Aeronautics Research Support Fund',
      awd_purpose:  'RESEARCH_SUPPORT',
      awd_fund_auth:'Funds may be used for research supplies, laboratory equipment, and graduate research support. PI: Kroo, Ilan.',
      awd_ctrl:     'Faculty Specific',
      awd_restrict: 'Highly Restricted',
      awd_role:     'PRINCIPAL_OWNER',
      awd_role_emp: 'Kroo, Ilan',
    },
    {
      awd_num:      'AWD-000205',
      awd_org:      'School of Engineering',
      awd_start:    '2020-05-01',
      awd_name:     'Engineering Faculty Travel Support Fund',
      awd_purpose:  'TRAVEL_SUPPORT',
      awd_fund_auth:'Supports faculty travel related to conferences and academic presentations. No equipment or supplies.',
      awd_ctrl:     'School Level',
      awd_restrict: 'Unrestricted',
      awd_role:     'PRINCIPAL_OWNER',
      awd_role_emp: 'Dean of Engineering',
    },
    {
      awd_num:      'AWD-000312',
      awd_org:      'School of Engineering',
      awd_start:    '2018-09-01',
      awd_name:     'Aerospace Engineering General Research Fund',
      awd_purpose:  'RESEARCH_SUPPORT',
      awd_fund_auth:'General research fund for aerospace engineering department. Supplies, equipment, and personnel eligible.',
      awd_ctrl:     'Departmental',
      awd_restrict: 'Restricted',
      awd_role:     'DEPARTMENTAL',
      awd_role_emp: 'Director of Research',
    },
  ],
};

// ── History persistence ────────────────────────────────────────────────────
const HISTORY_KEY = 'promptTesterHistory';
const MAX_HISTORY = 5;

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
}

function persistHistory(h) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(h));
}

// ── Helpers ────────────────────────────────────────────────────────────────
function scoreColor(val, max) {
  if (val == null) return 'var(--text-muted)';
  if (val < 0)     return 'var(--red, #ef4444)';
  if (val >= max)  return 'var(--green)';
  if (val > 0)     return 'var(--accent-hover)';
  return 'var(--text-muted)';
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function SemanticBar({ score }) {
  const pct   = Math.max(0, Math.min(1, (score || 0) / 2)) * 100;
  const color = score >= 1.5 ? 'var(--green)'
              : score >= 1.0 ? 'var(--accent-hover)'
              : score >= 0.5 ? 'var(--orange, #f59e0b)'
              : 'var(--text-muted)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 64, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontWeight: 700, fontSize: 11, color }}>{(score ?? 0).toFixed(1)}</span>
    </div>
  );
}

function CopyBtn({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="detail-toggle-btn"
      style={{ fontSize: 10, padding: '3px 8px' }}
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        });
      }}
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
}

function StatPill({ label, value, accent, warn }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
      borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border)',
      fontSize: 11,
    }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{
        fontWeight: 700,
        color: accent ? 'var(--accent-hover)' : warn ? 'var(--orange, #f59e0b)' : 'var(--text-primary)',
      }}>
        {value}
      </span>
    </div>
  );
}

// ── History panel ──────────────────────────────────────────────────────────
function HistoryPanel({ history, onRestore, onDelete, onClear }) {
  const [open, setOpen] = useState(true);

  if (history.length === 0) return null;

  return (
    <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
      {/* Collapsible header */}
      <div
        className="batch-exp-row"
        style={{
          padding: '8px 14px',
          borderBottom: open ? '1px solid var(--border)' : 'none',
          borderLeft: '3px solid rgba(139,0,0,0.35)',
          background: 'rgba(139,0,0,0.04)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(o => !o)}
        tabIndex={0}
        role="button"
        aria-expanded={open}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Recent Results</span>
          <span className="badge completed" style={{ fontSize: 10 }}>{history.length}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>LLM runs only · up to {MAX_HISTORY}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {open && (
            <button
              className="detail-toggle-btn"
              style={{ fontSize: 10, padding: '2px 8px' }}
              onClick={e => { e.stopPropagation(); onClear(); }}
            >
              Clear all
            </button>
          )}
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Rows */}
      {open && history.map((entry, idx) => (
        <div
          key={entry.id}
          onClick={() => onRestore(entry)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 14px',
            borderBottom: idx < history.length - 1 ? '1px solid var(--border)' : 'none',
            cursor: 'pointer',
            transition: 'background 0.1s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover, var(--bg-secondary))'}
          onMouseLeave={e => e.currentTarget.style.background = ''}
        >
          <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 14, textAlign: 'right', flexShrink: 0 }}>
            {idx + 1}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', minWidth: 106, flexShrink: 0 }}>
            {fmtDate(entry.savedAt)}
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.label}
          </span>
          <span className="badge completed" style={{ fontSize: 9, whiteSpace: 'nowrap', flexShrink: 0 }}>LLM</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {entry.result?.stats?.total_candidates ?? '?'} cands
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {(entry.result?.llm_usage?.total_tokens || 0).toLocaleString()} tok
          </span>
          <button
            className="detail-toggle-btn"
            style={{ fontSize: 10, padding: '2px 7px', color: 'var(--red, #ef4444)', borderColor: 'rgba(239,68,68,0.3)', flexShrink: 0 }}
            onClick={e => { e.stopPropagation(); onDelete(entry.id); }}
            title="Remove"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function PromptTester() {
  const [json, setJson]               = useState(JSON.stringify(SAMPLE, null, 2));
  const [result, setResult]           = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [activeView, setActiveView]   = useState('prompt');
  const [jsonError, setJsonError]     = useState(null);
  const [isLlmResult, setIsLlmResult] = useState(false);
  const [topK, setTopK]               = useState(5);

  // save state
  const [history, setHistory]         = useState(() => loadHistory());
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveLabel, setSaveLabel]     = useState('');
  const [savedFlash, setSavedFlash]   = useState(false);

  const textareaRef = useRef(null);

  function validateJson(val) {
    try { JSON.parse(val); setJsonError(null); return true; }
    catch (e) { setJsonError(e.message); return false; }
  }

  function autoLabel() {
    try {
      const p = JSON.parse(json);
      return p.expenditure?.exp_id || p.expenditure?.awd_num || 'Test result';
    } catch { return 'Test result'; }
  }

  const handlePreview = async (sendToLlm = false) => {
    if (!validateJson(json)) return;
    setError(null);
    setResult(null);
    setShowSaveForm(false);
    setSavedFlash(false);
    try {
      const payload = JSON.parse(json);
      payload.send_to_llm = sendToLlm;
      payload.top_k = topK;
      setLoading(true);
      const res = await testPrompt(payload);
      setResult(res);
      setIsLlmResult(sendToLlm);
      setActiveView(sendToLlm ? 'response' : 'prompt');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  function openSaveForm() {
    setSaveLabel(autoLabel());
    setShowSaveForm(true);
  }

  function confirmSave() {
    if (!saveLabel.trim() || !result) return;
    const entry = {
      id:      Date.now(),
      savedAt: new Date().toISOString(),
      label:   saveLabel.trim(),
      input:   json,
      result,
    };
    const updated = [entry, ...history].slice(0, MAX_HISTORY);
    setHistory(updated);
    persistHistory(updated);
    setShowSaveForm(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  }

  function handleRestore(entry) {
    setJson(entry.input);
    setResult(entry.result);
    setIsLlmResult(true);
    setActiveView('response');
    setShowSaveForm(false);
    setSavedFlash(false);
    setError(null);
  }

  function handleDelete(id) {
    const updated = history.filter(e => e.id !== id);
    setHistory(updated);
    persistHistory(updated);
  }

  const tabs = result
    ? [
        { id: 'prompt',     label: 'User Prompt' },
        { id: 'slim_exp',   label: 'Slim Expenditure' },
        { id: 'system',     label: 'System Prompt' },
        { id: 'candidates', label: `Rule Scores (${result.all_candidates?.length ?? 0})` },
        ...(result.llm_response ? [{
          id: 'response',
          label: result.llm_response_all && result.llm_response_all.length > result.llm_response.length
            ? `LLM Response (${result.llm_response.length} of ${result.llm_response_all.length})`
            : `LLM Response (${result.llm_response.length})`,
        }] : []),
        ...(result.chunk_log?.length ? [{ id: 'chunks', label: `Chunks (${result.chunk_log.length})` }] : []),
      ]
    : [];

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div className="section-title" style={{ marginBottom: 4 }}>LLM Prompt Tester</div>
        <div className="section-subtitle">
          Preview the exact payload sent to the LLM — including faculty name extraction,
          rule-based scores, and chunked pipeline behaviour. Matches production exactly.
        </div>
      </div>

      {/* ── History panel ─────────────────────────────────────────────── */}
      <HistoryPanel
        history={history}
        onRestore={handleRestore}
        onDelete={handleDelete}
        onClear={() => { setHistory([]); persistHistory([]); }}
      />

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

        {/* ── Left: Input ───────────────────────────────────────────────── */}
        <div style={{ flex: '0 0 420px', minWidth: 0 }}>
          <div className="card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                Input — expenditure + candidates
              </span>
              <button
                className="detail-toggle-btn"
                style={{ fontSize: 10, padding: '3px 8px' }}
                onClick={() => { setJson(JSON.stringify(SAMPLE, null, 2)); setJsonError(null); }}
              >
                Reset Sample
              </button>
            </div>

            <textarea
              ref={textareaRef}
              className="submit-editor"
              style={{
                minHeight: 460,
                fontFamily: 'monospace',
                fontSize: 12,
                borderColor: jsonError ? 'var(--red, #ef4444)' : undefined,
              }}
              value={json}
              onChange={e => { setJson(e.target.value); validateJson(e.target.value); }}
              spellCheck={false}
            />

            {jsonError && (
              <div style={{ fontSize: 11, color: 'var(--red, #ef4444)', marginTop: 4 }}>
                JSON error: {jsonError}
              </div>
            )}

            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                className="submit-btn"
                style={{ fontSize: 12, padding: '8px 18px' }}
                onClick={() => handlePreview(false)}
                disabled={loading || !!jsonError}
              >
                {loading ? 'Processing…' : 'Preview Prompt'}
              </button>
              <button
                className="submit-btn"
                style={{ fontSize: 12, padding: '8px 18px', background: 'linear-gradient(135deg, #22c55e, #06b6d4)' }}
                onClick={() => handlePreview(true)}
                disabled={loading || !!jsonError}
              >
                {loading ? 'Calling LLM…' : '▶ Send to LLM'}
              </button>
              {/* Top K control — only relevant for LLM calls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Top K</span>
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={topK}
                  onChange={e => setTopK(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="submit-editor"
                  style={{ width: 56, height: 34, padding: '0 8px', fontSize: 13, textAlign: 'center', borderRadius: 5 }}
                />
              </div>
            </div>

            {error && (
              <div className="submit-error" style={{ marginTop: 10 }}>
                {error}
              </div>
            )}
          </div>

          {/* Pipeline info */}
          <div className="card" style={{ padding: 12, marginTop: 12, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>Pipeline</div>
            <ol style={{ margin: 0, paddingLeft: 16 }}>
              <li>Rule scoring (all candidates)</li>
              <li>Slim expenditure (incl. <code>faculty_names</code>) + slim candidates</li>
              <li>Chunked LLM calls ({`≤`}CHUNK_SIZE per call) — LLM scores all four components</li>
              <li>Merge LLM scores → age tiebreaker → top-N</li>
            </ol>
          </div>
        </div>

        {/* ── Right: Output ─────────────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {result && (
            <>
              {/* Stats row */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                <StatPill label="Total"        value={result.stats.total_candidates} />
                <StatPill label="Unrestricted"  value={result.stats.unrestricted_separated} />
                <StatPill label="Rule-Scored"   value={result.stats.scored_candidates} />
                <StatPill label="To LLM"        value={result.stats.candidates_to_llm} accent />
                {result.stats.llm_scored != null && (
                  <StatPill label="LLM Scored" value={result.stats.llm_scored} />
                )}
                {result.stats.top_k != null && (
                  <StatPill
                    label="Top K"
                    value={`${result.stats.top_k_shown ?? result.stats.top_k} / ${result.stats.top_k}`}
                    accent={result.stats.top_k_shown < result.stats.top_k}
                  />
                )}
                <StatPill
                  label="Chunks"
                  value={`${result.stats.chunk_count} × ${result.stats.chunk_size}`}
                  warn={result.stats.chunk_count > 1}
                />
                <StatPill label="~Tokens" value={result.stats.estimated_prompt_tokens.toLocaleString()} />
                {result.llm_usage && (
                  <StatPill label="Used" value={`${(result.llm_usage.total_tokens || 0).toLocaleString()} tok`} accent />
                )}
              </div>

              {/* Tab bar + Save Result button */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: showSaveForm ? 6 : 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {tabs.map(t => (
                    <button
                      key={t.id}
                      className={`detail-panel-tab${activeView === t.id ? ' active' : ''}`}
                      onClick={() => setActiveView(t.id)}
                      style={{ fontSize: 11 }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Save Result — only shown for LLM results */}
                {isLlmResult && (
                  <button
                    className="detail-toggle-btn"
                    style={{
                      fontSize: 11, padding: '4px 12px', whiteSpace: 'nowrap',
                      color: savedFlash ? 'var(--green)' : undefined,
                      borderColor: savedFlash ? 'var(--green)' : undefined,
                    }}
                    onClick={savedFlash ? undefined : openSaveForm}
                    disabled={savedFlash}
                  >
                    {savedFlash ? '✓ Saved' : showSaveForm ? 'Cancel ×' : '↓ Save Result'}
                  </button>
                )}
              </div>

              {/* Inline save form */}
              {showSaveForm && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', marginBottom: 10,
                  background: 'var(--bg-secondary)', borderRadius: 7,
                  border: '1px solid var(--border)',
                  flexWrap: 'wrap',
                }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Label</span>
                  <input
                    className="submit-editor"
                    style={{ flex: '1 1 140px', minWidth: 120, maxWidth: 280, height: 28, padding: '0 8px', fontSize: 12, borderRadius: 5 }}
                    value={saveLabel}
                    onChange={e => setSaveLabel(e.target.value)}
                    placeholder="Name this result…"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') confirmSave();
                      if (e.key === 'Escape') setShowSaveForm(false);
                    }}
                  />
                  <button
                    className="submit-btn"
                    style={{ fontSize: 11, padding: '5px 14px' }}
                    onClick={confirmSave}
                    disabled={!saveLabel.trim()}
                  >
                    Save
                  </button>
                  <button
                    className="detail-toggle-btn"
                    style={{ fontSize: 11, padding: '4px 10px' }}
                    onClick={() => setShowSaveForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* ── User Prompt ── */}
              {activeView === 'prompt' && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>User Message</span>
                    <CopyBtn text={result.user_message} label="Copy JSON" />
                  </div>
                  <pre className="json-viewer" style={{ margin: 0, borderRadius: 0, maxHeight: 520, overflowY: 'auto', fontSize: 11 }}>
                    {result.user_message}
                  </pre>
                </div>
              )}

              {/* ── Slim Expenditure ── */}
              {activeView === 'slim_exp' && result.slim_expenditure && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Slimmed Expenditure sent to LLM</span>
                    <CopyBtn text={JSON.stringify(result.slim_expenditure, null, 2)} />
                  </div>
                  <div style={{ padding: 14 }}>
                    <div className="kv-grid" style={{ fontSize: 12, marginBottom: 12 }}>
                      {Object.entries(result.slim_expenditure)
                        .filter(([k]) => k !== 'faculty_names')
                        .map(([k, v]) => v ? (
                          <div key={k} className="kv-row">
                            <span className="kv-label" style={{ fontFamily: 'monospace', fontSize: 11 }}>{k}</span>
                            <span className="kv-value">{String(v)}</span>
                          </div>
                        ) : null)}
                    </div>
                    {result.slim_expenditure.faculty_names?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                          Extracted Faculty Names ({result.slim_expenditure.faculty_names.length})
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {result.slim_expenditure.faculty_names.map((n, i) => (
                            <span key={i} className="field-tag" style={{ fontSize: 12 }}>{n}</span>
                          ))}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                          These are passed to the LLM as <code>faculty_names</code> for name-matching.
                        </div>
                      </div>
                    )}
                    {(!result.slim_expenditure.faculty_names || result.slim_expenditure.faculty_names.length === 0) && (
                      <div className="warning-box" style={{ fontSize: 12, padding: '8px 12px' }}>
                        No faculty names extracted. Name matching will rely on award-side normalized_pi_name only.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── System Prompt ── */}
              {activeView === 'system' && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>System Prompt</span>
                    <CopyBtn text={result.system_prompt} />
                  </div>
                  <pre className="json-viewer" style={{ margin: 0, borderRadius: 0, maxHeight: 520, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 11 }}>
                    {result.system_prompt}
                  </pre>
                </div>
              )}

              {/* ── Rule Scores ── */}
              {activeView === 'candidates' && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    All {result.all_candidates?.length} candidates — rule scores before LLM
                  </div>
                  <div className="data-table-wrap" style={{ maxHeight: 500, overflowY: 'auto' }}>
                    <table className="data-table compact">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Award</th>
                          <th>Name</th>
                          <th title="Name match score">Nm</th>
                          <th title="Purpose match score">Pur</th>
                          <th title="Program match score">Prg</th>
                          <th title="Restriction bonus">Rst</th>
                          <th title="Total rule score">Total</th>
                          <th>Matched Fields</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.all_candidates?.map((c, i) => (
                          <tr key={c.awd_num}>
                            <td style={{ color: 'var(--text-muted)', fontSize: 10 }}>{i + 1}</td>
                            <td className="mono" style={{ fontWeight: 600 }}>{c.awd_num}</td>
                            <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{c.awd_name}</td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.name_match_score, 2) }}>{c.name_match_score ?? '-'}</td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.purpose_match_score, 3) }}>{c.purpose_match_score ?? '-'}</td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.program_match_score, 2) }}>{c.program_match_score ?? '-'}</td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.restriction_score, 2) }}>{c.restriction_score ?? '-'}</td>
                            <td style={{ fontWeight: 800, color: c.rule_score > 0 ? 'var(--accent-hover)' : 'var(--text-muted)' }}>
                              {typeof c.rule_score === 'number' ? c.rule_score.toFixed(1) : '-'}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                {c.matched_fields?.map((f, j) => (
                                  <span key={j} className="field-tag" style={{ fontSize: 9 }}>{f}</span>
                                ))}
                                {(!c.matched_fields || c.matched_fields.length === 0) && (
                                  <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!result.all_candidates?.length && (
                          <tr><td colSpan={9} className="empty-state" style={{ padding: 16 }}>No candidates</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── LLM Response ── */}
              {activeView === 'response' && result.llm_response && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Top {result.llm_response.length} of {result.llm_response_all?.length ?? result.llm_response.length} LLM-scored — sorted by total score + age
                    </span>
                    {result.llm_usage && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <StatPill label="Prompt"     value={(result.llm_usage.prompt_tokens || 0).toLocaleString()} />
                        <StatPill label="Completion" value={(result.llm_usage.completion_tokens || 0).toLocaleString()} />
                        <StatPill label="Total"      value={(result.llm_usage.total_tokens || 0).toLocaleString()} accent />
                      </div>
                    )}
                  </div>
                  <div className="data-table-wrap" style={{ maxHeight: 500, overflowY: 'auto' }}>
                    <table className="data-table compact">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Award</th>
                          <th title="name_match_score">Nm</th>
                          <th title="purpose_match_score">Pur</th>
                          <th title="program_match_score">Prg</th>
                          <th title="semantic_score (0–2)">Semantic</th>
                          <th title="restriction_score">Rst</th>
                          <th title="Total = Nm+Pur+Prg+Sem+Rst">Total</th>
                          <th>Explanation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.llm_response.map(c => (
                          <tr key={c.award_number}>
                            <td style={{ color: 'var(--text-muted)', fontSize: 10 }}>{c.rank ?? '—'}</td>
                            <td className="mono" style={{ fontWeight: 600 }}>{c.award_number}</td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.name_match_score, 2) }}>{c.name_match_score ?? '-'}</td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.purpose_match_score, 3) }}>{c.purpose_match_score ?? '-'}</td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.program_match_score, 2) }}>{c.program_match_score ?? '-'}</td>
                            <td style={{ minWidth: 110 }}><SemanticBar score={c.semantic_score} /></td>
                            <td style={{ fontWeight: 700, color: scoreColor(c.restriction_score, 2) }}>{c.restriction_score ?? '-'}</td>
                            <td style={{ fontWeight: 800, color: c.total_score >= 5 ? 'var(--green)' : c.total_score >= 2 ? 'var(--accent-hover)' : 'var(--text-muted)' }}>
                              {typeof c.total_score === 'number' ? c.total_score.toFixed(1) : '-'}
                            </td>
                            <td style={{ fontSize: 11, color: 'var(--text-secondary)', maxWidth: 320, minWidth: 180 }}>
                              {c.explanation || '—'}
                            </td>
                          </tr>
                        ))}
                        {result.llm_response.length === 0 && (
                          <tr><td colSpan={7} className="empty-state" style={{ padding: 16 }}>LLM returned no candidates</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Chunk Log ── */}
              {activeView === 'chunks' && result.chunk_log && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Chunk breakdown
                  </div>
                  <div className="data-table-wrap">
                    <table className="data-table compact">
                      <thead>
                        <tr><th>#</th><th>Candidates scored</th><th>Tokens used</th></tr>
                      </thead>
                      <tbody>
                        {result.chunk_log.map((chunk, i) => (
                          <tr key={i}>
                            <td style={{ color: 'var(--text-muted)' }}>{i + 1}</td>
                            <td style={{ fontWeight: 600 }}>{chunk.candidates_in}</td>
                            <td style={{ color: 'var(--accent-hover)', fontWeight: 600 }}>{(chunk.tokens || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {!result && !loading && !error && (
            <div className="card" style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 12 }}>🔬</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Ready to test</div>
              <div className="section-subtitle">
                Click <strong>Preview Prompt</strong> to inspect the exact JSON sent to the LLM,
                or <strong>Send to LLM</strong> to run the full chunked pipeline and see scores.
              </div>
            </div>
          )}

          {loading && (
            <div className="card" style={{ padding: 40, textAlign: 'center' }}>
              <div className="processing-indicator" style={{ justifyContent: 'center' }}>
                <div className="spinner" />
                <span>Running pipeline…</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
